import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { IndustryType, MinifiedDispensary } from '@gcv/shared';
import { DueDiligenceStatus, NonCannabisIndustryTypes } from '@gcv/shared';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box } from '@mui/system';

import { ReactComponent as NonCannabisIcon } from 'assets/images/store-location-generic-icon.svg';
import { ReactComponent as DispensaryIcon } from 'assets/images/store-location-icon.svg';
import { useComponent } from 'hooks/useComponent';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import type { Column, Row } from 'ui';
import { Button, Container, ContainerItem, Spinner, Table, TableContainer, Text, ZeroState } from 'ui';
import { DateTimeHelpers } from 'util/dateTime.util';
import { getLicenseType } from 'util/org.util';
import { capitalizeWords } from 'util/strings.util';
import { actions } from '../actions';
import { ChangeStatusModal } from '../components/change-status-modal';
import { model } from '../model';

interface ReviewCrbRow {
  id: string;
  name: string;
  parent_muo_name?: string;
  city: string;
  state: string;
  license_type: string;
  submitted_on: string;
  relationship_manager: string;
  due_diligence: string;
  industryType?: IndustryType;
}

export const AwaitingReview: React.FC = useComponent(() => {
  const navigate = useNavigate();
  const dispensaryStore = getFiDispensaryStore();
  const bankStore = getFiBankStore();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const appViewStore = getAppViewStateStore();

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState<boolean>(false);

  const columns: Column<ReviewCrbRow>[] = [
    {
      label: 'Name',
      id: 'name',
      widthPercent: 20,
      format: (row) => (
        <span style={{ display: 'flex' }}>
          {/* need to change icon here when we have information about it being an muo */}
          {/* <MuoIcon style={{ marginRight: '9px', width: '20px', height: '20px'  }}></MuoIcon> */}
          {row.industryType && NonCannabisIndustryTypes.includes(row.industryType) ? (
            <NonCannabisIcon style={{ marginRight: '9px', width: '20px', height: '20px' }} />
          ) : (
            <DispensaryIcon style={{ marginRight: '9px', width: '20px', height: '20px' }}></DispensaryIcon>
          )}
          {row.parent_muo_name ? (
            <Box flex="1">
              <Container padding="0" column justify="flex-start">
                <Text type="body3" content={row.name} />
                <Text
                  type="body3"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                  content={row.parent_muo_name}
                />
              </Container>
            </Box>
          ) : (
            <Box flex="1">
              <Text type="body3" content={row.name} />
            </Box>
          )}
        </span>
      ),
      sortFunction: (a, b) => {
        return a.data.name.toLowerCase() > b.data.name.toLowerCase() ? 1 : -1;
      }
    },
    {
      label: 'Relationship Manager',
      id: 'relationship_manager',
      widthPercent: 15
    },
    {
      label: 'City',
      id: 'city',
      widthPercent: 15,
      format: (row) => capitalizeWords(row.city)
    },
    {
      label: 'State',
      id: 'state',
      widthPercent: 10
    },
    {
      label: 'License Type',
      id: 'license_type',
      widthPercent: 15
    },
    {
      label: 'Due Diligence',
      id: 'due_diligence',
      widthPercent: 15,
      format: (row) => capitalizeWords(row.due_diligence)
    },
    {
      label: 'Submitted On',
      id: 'submitted_on',
      widthPercent: 20,
      format: (row) => {
        return row.submitted_on
          ? DateTimeHelpers.formatISOToTableDateString(row.submitted_on, bankStore.bank.iana_timezone)
          : '--';
      }
    }
  ];

  const filteredDispensaries = model.filterByAccountsSearchTerm(model.waitingDispensaries);

  actions.setAwaitingReviewCount(filteredDispensaries.length);

  const tableRows: Row<ReviewCrbRow>[] = filteredDispensaries.map((dispensary: MinifiedDispensary) => {
    const licenseType: string = getLicenseType(dispensary);
    const onboardingTemplates = getFiTemplateStore().templates.onboarding;
    const templateExists = Object.keys(onboardingTemplates).includes(
      dispensary.assigned_onboarding_template.template_id
    );

    return {
      id: dispensary.id,
      data: {
        id: dispensary.id,
        name: dispensary.name || '--',
        city: dispensary.city || '--',
        state: dispensary.state || '--',
        license_type: licenseType || '--',
        submitted_on: dispensary.assigned_onboarding_template.submitted_date || '',
        parent_muo_name: dispensaryStore.bankMuos.find((muo) => muo.id === dispensary.muo_id)?.name,
        relationship_manager: relationshipManagerStore.relationshipManagers[dispensary.id] || '--',
        due_diligence: templateExists
          ? onboardingTemplates[dispensary.assigned_onboarding_template.template_id].name
          : '--',
        industryType: dispensary.industryType
      }
    };
  });

  const showFilterZeroState = React.useMemo(() => {
    return tableRows.length === 0 && (model.accountsSearchTerm.length > 0 || model.searchTerms.length > 0);
  }, [tableRows, model.accountsSearchTerm.length]);

  const handleOnView = (id: string) => {
    navigate(`/secure/fi/accounts/${id}/review`);
  };

  if (model.isSorting) {
    return <Spinner />;
  }

  return (
    <>
      <Box>
        {showFilterZeroState || model.waitingDispensaries.length ? (
          <TableContainer>
            <Table
              columns={columns}
              rows={tableRows}
              dataCy="awaiting-review-table"
              defaultSort={
                appViewStore.accountPaginationFilters.awaitingReview.defaultSort
                  ? (appViewStore.accountPaginationFilters.awaitingReview.defaultSort as keyof ReviewCrbRow)
                  : 'submitted_on'
              }
              defaultSortDirection={
                appViewStore.accountPaginationFilters.awaitingReview.defaultSortDirection
                  ? appViewStore.accountPaginationFilters.awaitingReview.defaultSortDirection
                  : 'desc'
              }
              onSort={(sort, sortDirection) => {
                appViewStore.accountPaginationFilters.awaitingReview.defaultSort = sort;
                appViewStore.accountPaginationFilters.awaitingReview.defaultSortDirection = sortDirection;
              }}
              defaultRowCount={
                appViewStore.accountPaginationFilters.awaitingReview.pageSize
                  ? appViewStore.accountPaginationFilters.awaitingReview.pageSize
                  : 10
              }
              onRowsPerPageChange={(pageSize: number) => {
                appViewStore.accountPaginationFilters.awaitingReview.pageSize = pageSize;
              }}
              onClick={handleOnView}
              clickable
              showFilterZeroState={showFilterZeroState}
              page={appViewStore.accountPaginationFilters.awaitingReview.page}
              onPageChange={(page) => {
                appViewStore.accountPaginationFilters.awaitingReview.page = page;
              }}
              selectable={model.canUserChangeStatus}
              returnSelection={(selected) => {
                setSelectedAccounts(selected);
              }}
              actions={
                <Container padding="0" align="center">
                  <ContainerItem>
                    <Text type="table-header" content={`${selectedAccounts.length} Selected`} />
                  </ContainerItem>

                  <Button
                    label={'Change Status'}
                    onClick={() => setChangeStatusModalOpen(true)}
                    color={'text'}
                    startIcon={<SwapHorizIcon />}
                  />
                </Container>
              }
            />
          </TableContainer>
        ) : (
          <ZeroState
            title={'You have no accounts that are awaiting review'}
            subTitle={"Don't worry; Green Check will notify you when there is a new account to review!"}
            icon={'/img/empty-states/awaiting-review-account.svg'}
          />
        )}
      </Box>
      <ChangeStatusModal
        isOpen={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        initialStatus={DueDiligenceStatus.BANK_AWAITING_REVIEW}
      />
    </>
  );
});
